import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import {
  Button,
  Header,
  Card,
  Icon,
  Label,
  List,
  Message,
  Divider,
} from "semantic-ui-react";
import { getAuthToken, getClientAud } from "../selectors";

const panelStyle = {
  background: "#fff",
  color: "#1a1a1a",
  borderRadius: 12,
  border: "1px solid rgba(0, 0, 0, 0.1)",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06), 0 8px 28px rgba(0, 0, 0, 0.06)",
  padding: "1.75rem 2rem 2rem",
  marginBottom: "2rem",
};

const paymentSectionStyle = {
  background: "#fafafa",
  borderRadius: 10,
  padding: "1.25rem 1.35rem",
  marginBottom: "1rem",
  border: "1px solid rgba(0, 0, 0, 0.08)",
};

const plans = [
  {
    key: "1m",
    months: 1,
    label: "1 Monat",
    price: 20,
    perMonth: 20,
    features: [
      "Voller Chat-Zugang für 1 Monat",
      "Automatische Verlängerung (kündbar)",
      "E-Mail-Support",
    ],
  },
  {
    key: "6m",
    months: 6,
    label: "6 Monate",
    price: 50,
    perMonth: 50 / 6,
    features: [
      "Voller Chat-Zugang für 6 Monate",
      "Spare gegenüber dem Monatsabo",
      "Priorisierter Support",
    ],
  },
  {
    key: "12m",
    months: 12,
    label: "12 Monate",
    price: 70,
    perMonth: 70 / 12,
    recommended: true,
    features: [
      "Voller Chat-Zugang für 12 Monate",
      "Bestes Preis‑Leistungs‑Verhältnis",
      "Priorisierter Support",
    ],
  },
];

function authHeaders(authToken, clientAud, json = false) {
  const h = {
    ...(authToken ? {
      Authorization: `Bearer ${authToken}`,
      JWT_AUD: clientAud,
    } : {}),
  };
  if (json) h["Content-Type"] = "application/json";
  return h;
}

function planByKey(key) {
  return plans.find((p) => p.key === key) || null;
}

export default function SubscriptionPage() {
  const authToken = useSelector(getAuthToken);
  const clientAud = useSelector(getClientAud);
  const [stripe, setStripe] = useState(null);
  const [loadingKey, setLoadingKey] = useState(false);
  const [loadingCheckout, setLoadingCheckout] = useState(null);
  const [error, setError] = useState(null);
  /** 1 = Tarif wählen, 2 = Zahlungsmethode */
  const [phase, setPhase] = useState(1);
  /** @type {null | "1m" | "6m" | "12m"} */
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paypalAvailable, setPaypalAvailable] = useState(false);
  const [paypalPlans, setPaypalPlans] = useState(null);
  const [paypalSdkReady, setPaypalSdkReady] = useState(false);
  const [paypalReason, setPaypalReason] = useState(null);
  const mountedRef = React.useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const confirmPaypal = useCallback(async (subscriptionId, planKey) => {
    const res = await fetch("/api/v1/subscriptions/paypal_confirm", {
      method: "POST",
      headers: {
        ...authHeaders(authToken, clientAud, true),
      },
      credentials: "include",
      body: JSON.stringify({ subscription_id: subscriptionId, plan: planKey }),
    });
    if (res.status === 401) {
      window.location.href = "/login";
      return;
    }
    if (!res.ok) {
      let msg = "PayPal-Bestätigung fehlgeschlagen.";
      try {
        const j = await res.json();
        if (j.error === "email_mismatch") msg = "PayPal-E-Mail stimmt nicht mit deinem Konto überein.";
        else if (j.message) msg = j.message;
      } catch (_) { /* ignore */ }
      if (mountedRef.current) setError(msg);
      return;
    }
    window.location.href = "/chat";
  }, [authToken, clientAud]);

  useEffect(() => {
    async function checkPaypal() {
      try {
        const res = await fetch("/api/v1/subscriptions/paypal_offer", {
          headers: authHeaders(authToken, clientAud),
          credentials: "include",
        });
        if (res.status === 401) {
          window.location.href = "/login";
          return;
        }
        if (!res.ok) return;
        const j = await res.json();
        if (mountedRef.current) {
          setPaypalAvailable(!!j.enabled);
          if (!j.enabled && j.reason === "plans_missing") {
            setPaypalReason("missing_plans");
          }
        }
      } catch (_) { /* ignore */ }
    }
    checkPaypal();
  }, [authToken, clientAud]);

  useEffect(() => {
    if (phase !== 2 || !paypalAvailable) return;
    let cancelled = false;
    setPaypalSdkReady(false);
    setPaypalPlans(null);
    async function loadPaypalSdk() {
      const res = await fetch("/api/v1/subscriptions/paypal_offer", {
        headers: authHeaders(authToken, clientAud),
        credentials: "include",
      });
      if (res.status === 401) {
        window.location.href = "/login";
        return;
      }
      if (!res.ok || cancelled) return;
      const j = await res.json();
      if (!j.enabled || !j.clientId || cancelled) return;
      if (mountedRef.current) setPaypalPlans(j.plans);
      const existing = document.getElementById("paypal-sdk-script");
      if (existing && window.paypal) {
        if (!cancelled && mountedRef.current) setPaypalSdkReady(true);
        return;
      }
      if (existing) existing.remove();
      const script = document.createElement("script");
      script.id = "paypal-sdk-script";
      script.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(j.clientId)}&vault=true&intent=subscription`;
      script.onload = () => {
        if (!cancelled && mountedRef.current) setPaypalSdkReady(true);
      };
      script.onerror = () => {
        if (mountedRef.current) setError("PayPal-Skript konnte nicht geladen werden.");
      };
      document.body.appendChild(script);
    }
    loadPaypalSdk();
    return () => { cancelled = true; };
  }, [phase, paypalAvailable, authToken, clientAud]);

  useEffect(() => {
    if (phase !== 2 || !paypalSdkReady || !window.paypal || !paypalPlans || !selectedPlan) return undefined;

    const host = document.getElementById("paypal-btn-selected");
    if (!host) return undefined;
    host.innerHTML = "";
    const planId = paypalPlans[selectedPlan];
    if (!planId) return undefined;

    const buttons = window.paypal.Buttons({
      style: { layout: "vertical", label: "subscribe" },
      createSubscription(_data, actions) {
        return actions.subscription.create({ plan_id: planId });
      },
      onApprove(data) {
        return confirmPaypal(data.subscriptionID, selectedPlan);
      },
      onError() {
        if (mountedRef.current) setError("PayPal-Fehler. Bitte erneut versuchen oder eine andere Zahlungsart wählen.");
      },
    });

    let rendered = null;
    if (buttons.isEligible()) {
      buttons.render("#paypal-btn-selected");
      rendered = buttons;
    }

    return () => {
      if (rendered) {
        try { rendered.close(); } catch (_) { /* ignore */ }
      }
      const el = document.getElementById("paypal-btn-selected");
      if (el) el.innerHTML = "";
    };
  }, [phase, paypalSdkReady, paypalPlans, selectedPlan, confirmPaypal]);

  useEffect(() => {
    async function checkStatus() {
      const res = await fetch("/api/v1/subscriptions/status", {
        headers: authHeaders(authToken, clientAud),
        credentials: "include",
      });
      if (res.status === 401) {
        window.location.href = "/login";
        return;
      }
      if (!res.ok) return;
      const json = await res.json();
      if (json.active) {
        window.location.replace("/chat");
      }
    }
    checkStatus();
  }, [authToken, clientAud]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    async function confirmSession() {
      const res = await fetch("/api/v1/subscriptions/confirm", {
        method: "POST",
        headers: {
          ...authHeaders(authToken, clientAud, true),
        },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId }),
      });
      if (res.status === 401) {
        window.location.href = "/login";
        return;
      }
      if (res.ok) {
        window.history.replaceState({}, document.title, "/subscribe");
        window.location.href = "/chat";
      } else if (mountedRef.current) {
        setError("Payment confirmation failed. Please contact support if already charged.");
      }
    }
    if (sessionId) { confirmSession(); }
  }, [authToken, clientAud]);

  useEffect(() => {
    async function loadStripe() {
      const res = await fetch("/api/v1/subscriptions/public_key", {
        headers: authHeaders(authToken, clientAud),
        credentials: "include",
      });
      if (res.status === 401) {
        window.location.href = "/login";
        return;
      }
      if (!res.ok) {
        setError(`Failed to load Stripe key (${res.status})`);
        setLoadingKey(false);
        return;
      }
      const json = await res.json();
      if (!json.publishableKey) {
        setError("Stripe publishable key is not configured. Please set STRIPE_PUBLISHABLE_KEY.");
        setLoadingKey(false);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://js.stripe.com/v3/";
      script.onload = () => {
        const s = window.Stripe(json.publishableKey);
        setStripe(s);
      };
      document.body.appendChild(script);
      setLoadingKey(false);
    }
    setLoadingKey(true);
    loadStripe();
  }, [authToken, clientAud]);

  /**
   * @param {"default" | "sepa"} stripeMode default = Stripe Checkout (Karte, Apple/Google Pay, Klarna, Amazon Pay, … je nach Dashboard)
   */
  async function startCheckout(planKey, stripeMode) {
    if (!stripe) return;
    setLoadingCheckout(stripeMode);
    const body = stripeMode === "sepa"
      ? { plan: planKey, stripe_payment: "sepa" }
      : { plan: planKey };
    const res = await fetch("/api/v1/subscriptions/checkout", {
      method: "POST",
      headers: {
        ...authHeaders(authToken, clientAud, true),
      },
      credentials: "include",
      body: JSON.stringify(body),
    });
    if (res.status === 401) {
      window.location.href = "/login";
      return;
    }
    if (!res.ok) {
      setError(`Checkout start failed (${res.status})`);
      setLoadingCheckout(null);
      return;
    }
    const json = await res.json();
    if (json.id) await stripe.redirectToCheckout({ sessionId: json.id });
    setLoadingCheckout(null);
  }

  function goToPaymentStep(planKey) {
    setSelectedPlan(planKey);
    setPhase(2);
    setError(null);
  }

  function goBackToPlans() {
    setPhase(1);
    setSelectedPlan(null);
    setError(null);
  }

  const selected = selectedPlan ? planByKey(selectedPlan) : null;

  return (
    <div className="container subscription-page" style={{ maxWidth: 960, margin: "0 auto" }}>
      <div style={panelStyle}>
        <Header as="h2" style={{ fontWeight: 600, marginBottom: 4, color: "#1a1a1a" }}>
          Abonnement wählen
        </Header>
        <p style={{ color: "#444", marginBottom: 8, fontSize: 15, lineHeight: 1.5 }}>
          {phase === 1
            ? "Wähle zuerst deinen Tarif. Im nächsten Schritt wählst du die Zahlungsmethode (Karte, Klarna, Amazon Pay, SEPA, PayPal – je nach Verfügbarkeit)."
            : "Wähle jetzt, wie du bezahlen möchtest. Anschließend wirst du ggf. zu Stripe oder PayPal weitergeleitet."}
        </p>
        <p style={{ marginBottom: 22, fontSize: 14 }}>
          <NavLink to="/subscriptions" style={{ color: "#00847e", fontWeight: 500 }}>
            <Icon name="setting" />
            {" "}
            Bereits gebucht? Abo verwalten
          </NavLink>
        </p>

        {error && (
          <Message
            error
            onDismiss={() => setError(null)}
            style={{ marginBottom: "1.25rem" }}
            header="Das hat nicht geklappt"
            content={error}
          />
        )}

        {phase === 1 && !paypalAvailable && paypalReason === "missing_plans" && (
          <Message warning compact style={{ marginBottom: "1.25rem" }}>
            PayPal ist derzeit nicht aktiv (Abrechnungspläne fehlen). Du kannst im nächsten Schritt mit Karte, Klarna, Amazon Pay oder SEPA über Stripe zahlen.
          </Message>
        )}

        {phase === 1 && (
          <>
            <Header as="h3" style={{ fontWeight: 600, fontSize: 17, color: "#1a1a1a", marginBottom: 16 }}>
              Schritt 1: Tarif wählen
            </Header>
            <Card.Group itemsPerRow={3} stackable doubling>
              {plans.map((p) => (
                <Card
                  key={p.key}
                  style={{
                    borderRadius: 12,
                    overflow: "hidden",
                    border: p.recommended ? "2px solid #00b5ad" : "1px solid rgba(0,0,0,0.1)",
                    boxShadow: p.recommended
                      ? "0 4px 20px rgba(0, 181, 173, 0.15)"
                      : "0 1px 4px rgba(0,0,0,0.06)",
                    background: "#fff",
                  }}
                >
                  {p.recommended && (
                    <Label color="teal" ribbon style={{ zIndex: 1 }}>
                      Beliebt
                    </Label>
                  )}
                  <Card.Content style={{ paddingTop: p.recommended ? "2rem" : "1.25rem" }}>
                    <Card.Header style={{ fontSize: 18, fontWeight: 600, color: "#1a1a1a" }}>
                      {p.label}
                    </Card.Header>
                    <Card.Meta style={{ marginTop: 12 }}>
                      <span style={{ fontSize: 28, fontWeight: 700, color: "#1a1a1a" }}>
                        {p.price.toFixed(0)}
                        {" "}
                        €
                      </span>
                      <span style={{ color: "#666", fontSize: 15 }}>
                        {" "}
                        / {p.months}
                        {" "}
                        {p.months === 1 ? "Monat" : "Monate"}
                      </span>
                    </Card.Meta>
                    <Card.Description style={{ marginTop: 14 }}>
                      <div style={{ fontSize: 14, color: "#555", marginBottom: 10 }}>
                        <Icon name="calendar alternate outline" color="teal" />
                        {" "}
                        <strong>{p.perMonth.toFixed(2)} €</strong>
                        {" "}
                        pro Monat (effektiv)
                      </div>
                      <List relaxed size="small" style={{ color: "#444" }}>
                        {p.features.map((f, idx) => (
                          <List.Item key={idx} icon="check" content={f} />
                        ))}
                      </List>
                    </Card.Description>
                  </Card.Content>
                  <Card.Content
                    extra
                    style={{
                      background: "#fafafa",
                      borderTop: "1px solid rgba(0,0,0,0.06)",
                      padding: "1rem 1.15rem",
                    }}
                  >
                    <Button
                      primary
                      fluid
                      className="cuddle"
                      icon
                      labelPosition="right"
                      onClick={() => goToPaymentStep(p.key)}
                    >
                      Diesen Tarif wählen
                      <Icon name="arrow right" />
                    </Button>
                  </Card.Content>
                </Card>
              ))}
            </Card.Group>
          </>
        )}

        {phase === 2 && selected && (
          <>
            <Button
              basic
              icon="arrow left"
              content="Anderen Tarif wählen"
              onClick={goBackToPlans}
              style={{ marginBottom: "1.25rem" }}
            />
            <Header as="h3" style={{ fontWeight: 600, fontSize: 17, color: "#1a1a1a", marginBottom: 12 }}>
              Schritt 2: Zahlungsmethode
            </Header>
            <div
              style={{
                marginBottom: "1.5rem",
                padding: "1rem 1.15rem",
                borderRadius: 10,
                border: "1px solid rgba(0,181,173,0.35)",
                background: "rgba(0,181,173,0.06)",
              }}
            >
              <div style={{ fontSize: 13, color: "#666", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
                Gewählter Tarif
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#1a1a1a" }}>
                {selected.label}
                {" "}
                ·
                {" "}
                {selected.price.toFixed(0)} € / {selected.months} {selected.months === 1 ? "Monat" : "Monate"}
              </div>
            </div>

            <div style={{ fontSize: 12, color: "#888", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 12, fontWeight: 600 }}>
              Alle Zahlungsarten an einem Ort
            </div>

            <div style={paymentSectionStyle}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 12 }}>
                <Icon name="credit card outline" size="large" color="teal" style={{ flexShrink: 0, marginTop: 2 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 16, color: "#1a1a1a", marginBottom: 6 }}>
                    Karte & digitale Zahlarten
                  </div>
                  <p style={{ margin: "0 0 12px", fontSize: 14, color: "#555", lineHeight: 1.55 }}>
                    Visa, Mastercard, American Express, Apple Pay, Google Pay, Link sowie – sofern in eurem Stripe-Konto aktiviert – z.&nbsp;B. Klarna und Amazon Pay. Du wirst zu Stripe weitergeleitet.
                  </p>
                  <Button
                    primary
                    className="cuddle"
                    icon
                    labelPosition="right"
                    loading={loadingKey || loadingCheckout === "default"}
                    disabled={!stripe}
                    onClick={() => startCheckout(selectedPlan, "default")}
                  >
                    Weiter zu Stripe
                    <Icon name="external alternate" />
                  </Button>
                </div>
              </div>
            </div>

            <div style={paymentSectionStyle}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 12 }}>
                <Icon name="university" size="large" color="teal" style={{ flexShrink: 0, marginTop: 2 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 16, color: "#1a1a1a", marginBottom: 6 }}>
                    SEPA-Lastschrift
                  </div>
                  <p style={{ margin: "0 0 12px", fontSize: 14, color: "#555", lineHeight: 1.55 }}>
                    Bankkonto per SEPA-Mandat. Die erste Abbuchung kann einige Werktage dauern; Zugang, sobald Stripe das Abo als aktiv meldet.
                  </p>
                  <Button
                    secondary
                    icon
                    labelPosition="right"
                    loading={loadingKey || loadingCheckout === "sepa"}
                    disabled={!stripe}
                    onClick={() => startCheckout(selectedPlan, "sepa")}
                  >
                    Weiter zur Lastschrift
                    <Icon name="external alternate" />
                  </Button>
                </div>
              </div>
            </div>

            {paypalAvailable && (
              <div style={paymentSectionStyle}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <Icon name="paypal" size="large" style={{ flexShrink: 0, marginTop: 2, color: "#0070ba" }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 16, color: "#1a1a1a", marginBottom: 6 }}>
                      PayPal
                    </div>
                    <p style={{ margin: "0 0 12px", fontSize: 14, color: "#555", lineHeight: 1.55 }}>
                      Abo direkt über PayPal. Nach Bestätigung wird dein Zugang freigeschaltet. Verwaltung im PayPal-Konto unter Zahlungen / Abonnements.
                    </p>
                    <div id="paypal-btn-selected" style={{ minHeight: 48 }} />
                  </div>
                </div>
              </div>
            )}

            <Divider style={{ margin: "1.75rem 0 1.25rem" }} />

            <Message info icon style={{ borderRadius: 8, marginBottom: 0 }}>
              <Icon name="info circle" />
              <Message.Content>
                <Message.Header style={{ marginBottom: 6 }}>Hinweis</Message.Header>
                <p style={{ margin: 0, lineHeight: 1.55, color: "#444" }}>
                  Kündigung und Rechnungen für Stripe-Abbos im Stripe-Kundenportal; für PayPal im PayPal-Konto. Welche Klarna-/Amazon-Pay-Optionen im Stripe-Fenster erscheinen, steuert ihr im Stripe-Dashboard.
                </p>
              </Message.Content>
            </Message>
          </>
        )}
      </div>
    </div>
  );
}
